import React, { useEffect, useState } from "react";
import { ArrowLeft, RefreshCw, UserCheck, Users, ThumbsUp, ThumbsDown, Mic2, Share2 } from "lucide-react";
import { fetchMyNetwork, toggleFollow } from "../lib/socialApi";

const STAT_CARDS = [
  ["followers", "Chi ti segue", Users],
  ["following", "Persone seguite", UserCheck],
  ["posts", "Pubblicazioni", Mic2],
  ["likesReceived", "Mi piace ricevuti", ThumbsUp],
  ["dislikesReceived", "Non mi piace ricevuti", ThumbsDown],
  ["sharesReceived", "Diffusioni dei tuoi post", Share2],
];

function Person({ person, canUnfollow, onUnfollow }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl border border-edge bg-white">
      {person.avatar ? (
        <img src={person.avatar} alt="" className="w-11 h-11 rounded-full object-cover border border-edge" />
      ) : (
        <div className="w-11 h-11 rounded-full bg-ink text-white flex items-center justify-center font-semibold">
          {(person.displayName || "?").slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm text-ink truncate">{person.displayName}</p>
        <p className="text-[11px] text-muted">Identità {person.identityId.slice(0, 8)}</p>
      </div>
      {canUnfollow && (
        <button className="action-btn" onClick={() => onUnfollow(person.identityId)}>
          Smetti di seguire
        </button>
      )}
    </div>
  );
}

function ReceivedShares({ items }) {
  return (
    <div className="rounded-2xl border border-edge p-4">
      <div className="flex items-center gap-2 mb-3">
        <Share2 size={18} className="text-primary" />
        <p className="font-semibold text-ink">Diffusioni dei tuoi post</p>
      </div>
      {items?.length ? (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={`${item.postId}-${item.sharedAt}`} className="rounded-xl bg-cream border border-edge p-3 text-sm">
              <p className="text-ink">
                <strong>{item.actorName}</strong> ha diffuso un tuo post
              </p>
              <p className="text-[11px] text-muted mt-1">
                Argomento: {item.topicSlug} · {new Date(item.sharedAt).toLocaleString("it-IT")}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted">Nessun tuo post è stato ancora diffuso.</p>
      )}
    </div>
  );
}

export function NetworkView({ onBack }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      setData(await fetchMyNetwork());
    } catch (e) {
      setError(e?.message || "Rete non disponibile");
    }
  };

  useEffect(() => { void load(); }, []);

  const unfollow = async (identityId) => {
    await toggleFollow(identityId, "unfollow");
    await load();
  };

  const openCard = (key) => {
    if (key === "followers") setTab("followers");
    else if (key === "following") setTab("following");
    else if (key === "sharesReceived") setTab("sharesReceived");
    else setTab("overview");
  };

  return (
    <main className="min-h-screen bg-cream px-3 sm:px-6 py-5 sm:py-8">
      <div className="max-w-5xl mx-auto">
        <button onClick={onBack} className="action-btn mb-4">
          <ArrowLeft size={14} /> Torna al social
        </button>

        <section className="bg-white border border-edge rounded-3xl p-4 sm:p-7 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-5">
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-semibold text-ink">La mia rete</h1>
              <p className="text-sm text-muted mt-1">Persone, pubblicazioni, reazioni e diffusioni reali del tuo profilo.</p>
            </div>
            <button onClick={load} className="action-btn">
              <RefreshCw size={15} /> Aggiorna
            </button>
          </div>

          {error && <p className="text-primary text-sm mb-4">{error}</p>}
          {!data && !error && <p className="text-muted text-sm">Caricamento della rete…</p>}

          {data && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 mb-6">
                {STAT_CARDS.map(([key, label, Icon]) => (
                  <button
                    key={key}
                    className="text-left rounded-2xl bg-cream border border-edge p-3"
                    onClick={() => openCard(key)}
                  >
                    <Icon size={17} className="text-primary mb-2" />
                    <strong className="block text-xl text-ink">{data.analytics[key] ?? 0}</strong>
                    <span className="text-[11px] text-muted">{label}</span>
                  </button>
                ))}
              </div>

              <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
                {[
                  ["overview", "Panoramica"],
                  ["sharesReceived", "Diffusioni dei tuoi post"],
                  ["followers", "Chi mi segue"],
                  ["following", "Persone che seguo"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    className={`action-btn whitespace-nowrap ${tab === key ? "!bg-ink !text-white" : ""}`}
                    onClick={() => setTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {tab === "overview" && (
                <div className="grid sm:grid-cols-2 gap-3">

                <div
                  className="sm:col-span-2 rounded-2xl border border-edge bg-white p-5"
                  data-testid="profile-statistics-chart"
                >
                  <div className="mb-5">
                    <h3 className="text-lg font-semibold text-ink">
                      Statistiche del profilo
                    </h3>

                    <p className="mt-1 text-sm text-muted">
                      Confronto dei dati attuali della rete e delle interazioni ricevute.
                    </p>
                  </div>

                  {(() => {
                    const chartData = [
                      {
                        label: "Chi ti segue",
                        value: Number(data.analytics?.followers || 0),
                      },
                      {
                        label: "Persone seguite",
                        value: Number(data.analytics?.following || 0),
                      },
                      {
                        label: "Pubblicazioni",
                        value: Number(data.analytics?.posts || 0),
                      },
                      {
                        label: "Mi piace ricevuti",
                        value: Number(data.analytics?.likesReceived || 0),
                      },
                      {
                        label: "Non mi piace ricevuti",
                        value: Number(data.analytics?.dislikesReceived || 0),
                      },
                      {
                        label: "Diffusioni",
                        value: Number(data.analytics?.sharesReceived || 0),
                      },
                    ];

                    const maxValue = Math.max(
                      1,
                      ...chartData.map((item) => item.value)
                    );

                    return (
                      <div className="space-y-4">
                        {chartData.map((item) => {
                          const width =
                            item.value <= 0
                              ? 0
                              : Math.max(
                                  4,
                                  Math.round((item.value / maxValue) * 100)
                                );

                          return (
                            <div key={item.label}>
                              <div className="mb-1.5 flex items-center justify-between gap-4">
                                <span className="text-sm font-medium text-ink">
                                  {item.label}
                                </span>

                                <strong className="text-sm text-ink">
                                  {item.value.toLocaleString("it-IT")}
                                </strong>
                              </div>

                              <div className="h-3 w-full overflow-hidden rounded-full bg-cream">
                                <div
                                  className="h-full rounded-full bg-primary transition-all duration-500"
                                  style={{ width: `${width}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                  <div className="rounded-2xl border border-edge p-4">
                    <p className="font-semibold text-ink">Reazioni date</p>
                    <p className="text-sm text-muted mt-2">Mi piace: <strong>{data.analytics.likesGiven}</strong></p>
                    <p className="text-sm text-muted">Non mi piace: <strong>{data.analytics.dislikesGiven}</strong></p>
                  </div>
                  <div className="rounded-2xl border border-edge p-4">
                    <p className="font-semibold text-ink">Reazioni ricevute</p>
                    <p className="text-sm text-muted mt-2">Mi piace: <strong>{data.analytics.likesReceived}</strong></p>
                    <p className="text-sm text-muted">Non mi piace: <strong>{data.analytics.dislikesReceived}</strong></p>
                  </div>
                </div>
              )}

              {tab === "sharesReceived" && <ReceivedShares items={data.sharesReceived} />}

              {tab === "followers" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.followers.length
                    ? data.followers.map((p) => <Person key={p.identityId} person={p} />)
                    : <p className="text-sm text-muted">Nessun follower al momento.</p>}
                </div>
              )}

              {tab === "following" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.following.length
                    ? data.following.map((p) => <Person key={p.identityId} person={p} canUnfollow onUnfollow={unfollow} />)
                    : <p className="text-sm text-muted">Non segui ancora nessuno.</p>}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
